import { useMutation, useQuery } from "@apollo/client";
import { Clock, Loader2 } from "lucide-react";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OpeningHoursForm } from "@/domains/settings/features/business/components/opening-hours-form";
import type { BusinessSettingsFormData } from "@/domains/settings/features/business/type/business-settings";
import type { DaySchedule } from "@/domains/settings/types";
import { BULK_UPDATE_OPERATING_HOURS, GET_SALON_OPERATING_HOURS } from "@/graphql/configuration";
import { UPDATE_GBP_HOURS_MUTATION } from "@/graphql/google-business";
import { useSalonStore } from "@/lib/store/salon";

interface GbpUpdateHoursDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GbpUpdateHoursDialog({ open, onOpenChange }: GbpUpdateHoursDialogProps) {
  const { t } = useTranslation("reviews");
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  const { data: hoursData, loading: hoursLoading } = useQuery(GET_SALON_OPERATING_HOURS, {
    variables: { salonId: salonId ?? "" },
    skip: !salonId || !open,
    fetchPolicy: "network-only",
  });

  const defaultValues: BusinessSettingsFormData = useMemo(
    () => ({
      basicInfo: {
        name: "",
        address: "",
        additionalAddressInfo: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
      },
      contactInfo: { emails: [], phones: [] },
      operatingHours: { weeklyHours: hoursData?.getSalonOperatingHours?.weeklyHours ?? [] },
    }),
    [hoursData]
  );

  const form = useForm<BusinessSettingsFormData>({ defaultValues });

  // Reset form whenever fresh data arrives
  useEffect(() => {
    if (hoursData) form.reset(defaultValues);
  }, [hoursData, defaultValues, form.reset]); // eslint-disable-line react-hooks/exhaustive-deps

  const [bulkUpdateHours] = useMutation(BULK_UPDATE_OPERATING_HOURS);
  const [updateGbpHours] = useMutation(UPDATE_GBP_HOURS_MUTATION);

  const saving = form.formState.isSubmitting;

  const handleSave = form.handleSubmit(async (values) => {
    if (!salonId) return;
    const weeklyHours: DaySchedule[] = values.operatingHours.weeklyHours;

    try {
      // Update our DB and Google simultaneously
      await Promise.all([
        bulkUpdateHours({
          variables: {
            salonId,
            input: {
              operatingHours: weeklyHours.map((day) => ({
                dayOfWeek: day.dayOfWeek,
                timeSlots: day.timeSlots.map((slot) => ({
                  id: slot.id,
                  startTime: slot.startTime,
                  endTime: slot.endTime,
                  slotOrder: slot.slotOrder,
                })),
              })),
            },
          },
          refetchQueries: ["GetSalonOperatingHours"],
        }),
        updateGbpHours({
          variables: {
            salonId,
            weeklyHours: weeklyHours.map((day) => ({
              dayOfWeek: day.dayOfWeek,
              timeSlots: day.timeSlots.map((slot) => ({
                startTime: slot.startTime,
                endTime: slot.endTime,
              })),
            })),
          },
        }),
      ]);

      toast.success(t("gbpUpdateHours.toastSuccess"));
      onOpenChange(false);
    } catch {
      toast.error(t("gbpUpdateHours.toastError"));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {t("gbpUpdateHours.dialogTitle")}
          </DialogTitle>
          <DialogDescription>{t("gbpUpdateHours.dialogDescription")}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2">
          <FormProvider {...form}>
            <OpeningHoursForm loading={hoursLoading && !hoursData} saving={saving} bare />
          </FormProvider>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("gbpUpdateHours.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saving || hoursLoading}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("gbpUpdateHours.saveHours")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
